import { ChevronLeft, ChevronRight, NotebookPen, Plus, Printer } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { formatDecimalHours, formatMinutes } from "../../shared/duration";
import { dateKeyInZone, normalizeTimeZone } from "../../shared/time-zone";
import { addDays, WEEKDAY_LABELS, weekStartOf, weekdayOf } from "../../shared/week";
import type { RouterOutputs, UserSession } from "../lib/trpc";
import { trpc } from "../lib/trpc";

type WorkLogDay = RouterOutputs["workLog"]["week"]["days"][number];
type WorkLogRow = WorkLogDay["rows"][number];

function giornoLeggibile(dateKey: string) {
  const [anno, mese, giorno] = dateKey.split("-").map(Number);
  return new Date(Date.UTC(anno, mese - 1, giorno)).toLocaleDateString("it-IT", { day: "numeric", month: "short", timeZone: "UTC" });
}

function periodoLeggibile(weekStart: string) {
  return `${giornoLeggibile(weekStart)} - ${giornoLeggibile(addDays(weekStart, 6))}`;
}

function orario(valore: Date | string | null) {
  if (!valore) return "in corso";
  return new Date(valore).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
}

/** Che lavoro era: la struttura quando c'e, altrimenti la voce libera scritta a mano. */
function lavoroDi(riga: WorkLogRow) {
  if (riga.siteName) return riga.siteName;
  return riga.description ?? "Voce libera";
}

/**
 * Registro ore settimanale.
 *
 * E il foglio di carta che gli operatori compilano oggi, messo in app: una riga per lavoro,
 * i sette giorni da lunedi a domenica, il totale del giorno e quello della settimana in ore
 * decimali, come li scrivono loro. Le ore non si inventano qui: arrivano dai timer gia
 * registrati sugli interventi. L'unica scrittura che questa pagina consente e la voce libera,
 * cioe il pezzo di giornata che sul foglio c'e e nell'app non c'era: posta, ufficio, garage.
 *
 * La stampa e quella del browser: `@media print` toglie menu, filtri e comandi e lascia
 * intestazione, giorni, righe e totali.
 */
export function WorkLogPage({ session }: { session: UserSession }) {
  const timeZone = normalizeTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const utils = trpc.useUtils();
  const oggi = dateKeyInZone(new Date(), timeZone);
  const [weekStart, setWeekStart] = useState(() => weekStartOf(oggi));
  const [userId, setUserId] = useState(session.user.id);
  const [openForm, setOpenForm] = useState(false);
  const [esito, setEsito] = useState<string | null>(null);

  const people = trpc.workLog.people.useQuery();
  const week = trpc.workLog.week.useQuery({ userId, weekStart, timeZone });
  const addFreeEntry = trpc.workLog.addFreeEntry.useMutation();

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setEsito(null);
    const form = event.currentTarget;
    const data = new FormData(form);
    const giorno = String(data.get("day") || oggi);
    const dalle = String(data.get("startTime") || "");
    const alle = String(data.get("endTime") || "");
    const descrizione = String(data.get("description") || "").trim();
    if (!dalle || !alle) { setEsito("Indica l'ora di inizio e quella di fine."); return; }
    if (descrizione.length < 3) { setEsito("Scrivi che cosa hai fatto."); return; }
    try {
      await addFreeEntry.mutateAsync({
        userId,
        // L'orario scritto e quello del telefono di chi compila: `datetime-local` senza fuso,
        // esattamente come le ore manuali di un intervento.
        startedAt: new Date(`${giorno}T${dalle}`),
        endedAt: new Date(`${giorno}T${alle}`),
        description: descrizione,
      });
      form.reset();
      setOpenForm(false);
      await utils.workLog.week.invalidate();
      setEsito("Voce registrata.");
    } catch (error) {
      setEsito(error instanceof Error ? error.message : "Registrazione non riuscita.");
    }
  }

  const giorni = week.data?.days ?? [];
  const operatore = week.data?.userName ?? session.user.name;

  return (
    <main className="page work-log">
      <header className="page-heading work-log__heading">
        <div>
          <p className="eyebrow">Ore della settimana</p>
          <h1>Registro ore</h1>
          <p>Quello che il foglio settimanale raccoglie a mano: giorno per giorno, con i totali.</p>
        </div>
        <div className="page-heading__actions">
          {people.data && people.data.length > 1 && (
            <label className="inline-field">
              <span>Operatore</span>
              <select value={userId} onChange={(event) => setUserId(event.target.value)}>
                {people.data.map((persona) => <option key={persona.userId} value={persona.userId}>{persona.name}</option>)}
              </select>
            </label>
          )}
          <div className="week-nav" role="group" aria-label="Cambia settimana">
            <button type="button" className="icon-button" aria-label="Settimana precedente" onClick={() => setWeekStart(addDays(weekStart, -7))}><ChevronLeft size={20} /></button>
            <button type="button" className="button button--ghost" onClick={() => setWeekStart(weekStartOf(oggi))}>Questa settimana</button>
            <button type="button" className="icon-button" aria-label="Settimana successiva" onClick={() => setWeekStart(addDays(weekStart, 7))}><ChevronRight size={20} /></button>
          </div>
          <button type="button" className="button button--outline" onClick={() => window.print()}><Printer size={18} /> Stampa</button>
          <button type="button" className="button button--primary" onClick={() => { setOpenForm(!openForm); setEsito(null); }}><Plus size={19} /> Voce libera</button>
        </div>
      </header>

      {/* L'intestazione della stampa: sul foglio deve esserci scritto di chi e la settimana. */}
      <div className="work-log__stamp">
        <strong>{operatore}</strong>
        <span>Settimana dal {periodoLeggibile(weekStart)}</span>
      </div>

      {openForm && (
        <form className="editor-card editor-card--compact work-log__form" onSubmit={submit}>
          <div className="editor-card__heading">
            <h2>Voce libera</h2>
            <p>Ore di lavoro senza struttura: posta, ufficio, garage. Restano nel registro come tutte le altre.</p>
          </div>
          <div className="form-grid">
            <label><span>Giorno</span><input name="day" type="date" defaultValue={oggi} min={weekStart} max={addDays(weekStart, 6)} /></label>
            <label><span>Dalle</span><input name="startTime" type="time" required /></label>
            <label><span>Alle</span><input name="endTime" type="time" required /></label>
            <label className="form-grid__wide"><span>Che cosa</span><input name="description" minLength={3} required placeholder="Ritiro posta e giro in ufficio" /></label>
          </div>
          <div className="form-actions">
            <button type="button" className="button button--ghost" onClick={() => setOpenForm(false)}>Annulla</button>
            <button className={`button button--primary${addFreeEntry.isPending ? " button--saving" : ""}`}>
              {addFreeEntry.isPending ? "Salvataggio..." : "Registra la voce"}
            </button>
          </div>
        </form>
      )}
      {esito && <p className="notice" role="status">{esito}</p>}

      {week.isLoading && <p className="loading-copy">Caricamento registro...</p>}
      {!week.isLoading && (
        <section className="work-log__week">
          {giorni.map((giorno) => (
            <article className={`work-log__day${giorno.dateKey === oggi ? " work-log__day--oggi" : ""}`} key={giorno.dateKey}>
              <header>
                <h2>{WEEKDAY_LABELS[weekdayOf(giorno.dateKey)]} <span>{giornoLeggibile(giorno.dateKey)}</span></h2>
                <span className="work-log__day-total">{formatDecimalHours(giorno.minutes)} h</span>
              </header>
              {giorno.rows.length === 0
                ? <p className="work-log__empty">Nessuna ora registrata.</p>
                : (
                  <table className="work-log__table">
                    <thead>
                      <tr>
                        <th scope="col">Dalle</th>
                        <th scope="col">Alle</th>
                        <th scope="col">Struttura o voce</th>
                        <th scope="col">Intervento</th>
                        <th scope="col">Nota</th>
                        <th scope="col">Durata</th>
                      </tr>
                    </thead>
                    <tbody>
                      {giorno.rows.map((riga) => (
                        <tr key={riga.id}>
                          <td>{orario(riga.startedAt)}</td>
                          <td>{orario(riga.endedAt)}</td>
                          <td>{lavoroDi(riga)}</td>
                          <td>
                            {riga.interventionId
                              ? <Link to={`/interventi/${riga.interventionId}`}>{riga.interventionTitle}</Link>
                              : <span className="work-log__free">voce libera</span>}
                          </td>
                          {/* La descrizione di una voce libera e gia il suo "lavoro": qui resta
                              soltanto il motivo di una correzione o di ore messe a mano. */}
                          <td>{riga.reason ?? ""}</td>
                          <td>{riga.minutes == null ? "in corso" : formatMinutes(riga.minutes)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
            </article>
          ))}
          <footer className="work-log__total">
            <span><NotebookPen size={18} aria-hidden="true" /> Totale settimana</span>
            <strong>{formatDecimalHours(week.data?.minutes ?? 0)} h</strong>
          </footer>
          {week.data?.hasOpenEntry && (
            <p className="form-hint">C'è un timer ancora aperto in questa settimana: il totale lo conterà quando verrà fermato.</p>
          )}
        </section>
      )}
    </main>
  );
}
